import React, { useMemo, useState } from "react";
import { Popover, CircularProgress } from "@mui/material";
import { KeyboardArrowDown, Close, Check } from "@mui/icons-material";
import "./CompactFilters.css";

/**
 * A small, self-contained single-select dropdown matching CompactMultiSelect
 * visually (shared CompactFilters.css). Mirrors react-select's onChange
 * contract on purpose — onChange receives the full selected option object
 * (or null when cleared) — so it's a drop-in replacement for the toolbar's
 * react-select instances without touching any of their existing handlers.
 *
 * Rendering the menu via MUI's Popover (portals to document.body, high
 * z-index by default) also fixes those dropdowns opening underneath the
 * table — the react-select menus weren't portaled and could end up behind
 * the table's sticky header.
 */
function CompactSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  isClearable = false,
  isDisabled = false,
  isLoading = false,
  isSearchable = true,
  noOptionsMessage = "No options",
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !search.trim()) return options;
    const term = search.trim().toLowerCase();
    return options.filter((opt) => String(opt.label ?? "").toLowerCase().includes(term));
  }, [options, search, isSearchable]);

  const handleOpen = (e) => {
    if (isDisabled) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const handleSelect = (opt) => {
    onChange(opt);
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <>
      <button
        type="button"
        className={`compact-filter-trigger${value ? " has-value" : ""}${isDisabled ? " is-disabled" : ""}`}
        onClick={handleOpen}
        disabled={isDisabled}
      >
        <span className="compact-filter-trigger-label">{value?.label ?? placeholder}</span>
        <span className="compact-filter-trigger-icons">
          {isLoading ? (
            <CircularProgress size={13} thickness={5} />
          ) : (
            <>
              {isClearable && value ? (
                <span className="compact-filter-clear" onClick={handleClear}>
                  <Close />
                </span>
              ) : null}
              <KeyboardArrowDown className="compact-filter-chevron" />
            </>
          )}
        </span>
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: "compact-filter-menu" }}
      >
        {isSearchable && (
          <div className="compact-filter-search">
            <input
              autoFocus
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((opt) => {
            const isSelected = value?.value === opt.value;
            return (
              <div
                key={opt.value}
                className={`compact-filter-option${isSelected ? " is-selected" : ""}`}
                onClick={() => handleSelect(opt)}
              >
                <span className="compact-filter-option-row">
                  {opt.label}
                  {isSelected && <Check className="compact-filter-option-check" />}
                </span>
              </div>
            );
          })
        ) : (
          <div className="compact-filter-empty">{noOptionsMessage}</div>
        )}
      </Popover>
    </>
  );
}

export default CompactSelect;
